/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-return */
import * as z from 'zod'
import { getTransformObject } from '~/common/photos/transforms'
import { getLangFallbackUrl, langFallback$Fetch } from '~/lang/utils/langfetch'
import { childAgeCSVStringToNumberArray } from '~/utility/child-ages/child-ages'
import { getGeoPointFromWKTPoint } from '~/utility/geo/GeoLocation'
import { toCamel } from '~/utility/toCamel'
import { csrfTokenService } from '../csrf/CsrfTokenService'
import {
  UserBookingsFetchError,
  UserBookingsValidationError,
} from './UserBookingsError'
import {
  UserBooking,
  UserBookingCampsitePhoto,
  UserBookingReview,
  UserBookingSummary,
} from './schemas'

export class UserBookingsRepositoryFetch {
  async cancel(
    langCode: string,
    prettyId: string,
    cancellationReason: string,
  ): Promise<{ status: number }> {
    const response = await this.getCancelResponse(
      langCode,
      prettyId,
      cancellationReason,
    )
    if (response.user_bookings_cancel.errors) {
      throw parseCancelResponseErrors(response.user_bookings_cancel.errors)
    }
    return {
      status: Number(response.user_bookings_cancel.status),
    }
  }

  private async getCancelResponse(
    langCode: string,
    prettyId: string,
    cancellationReason: string,
  ): Promise<CancelResponse> {
    const { csrfToken } = await csrfTokenService()
    const url = `/account/bookings/${prettyId}/cancel/`

    const response = await $fetch<string>(getLangFallbackUrl(langCode, url), {
      method: 'POST',
      body: this.getCancelFormData(cancellationReason),
      headers: {
        'X-ELDARION-AJAX': 'true',
        'X-CSRFToken': csrfToken,
      },
      credentials: 'include',
    })
    return JSON.parse(response) as CancelResponse
  }

  private getCancelFormData(cancellationReason: string) {
    const formData = new FormData()
    formData.append('cancellation_reason', cancellationReason)
    return formData
  }

  async resendBookingConfirmation(
    langCode: string,
    prettyId: string,
  ): Promise<void> {
    const url = `/_/user-bookings/resend-booking-confirmation/${prettyId}/`
    const { csrfToken } = await csrfTokenService()
    await langFallback$Fetch(langCode, url, {
      method: 'POST',
      headers: {
        'X-CSRFToken': csrfToken,
      },
    })
  }

  async readOneByPrettyId(
    langCode: string,
    prettyId: string,
  ): Promise<UserBooking> {
    const response = await langFallback$Fetch(
      langCode,
      `/_/user-bookings/${prettyId}/`,
    )
    return userBookingTransformer.parse(response) as UserBooking
  }

  async readAll(langCode: string): Promise<UserBookingSummary[]> {
    const url = '/_/user-bookings/'
    let response: UserBookingsSummaryResponse

    // Step 1: Fetch the data
    try {
      response = await langFallback$Fetch<UserBookingsSummaryResponse>(
        langCode,
        url,
        {
          timeout: 30000,
        },
      )
    } catch (error) {
      // Silently return empty response for abort errors

      if (error?.name === 'AbortError' || error?.message?.includes('aborted')) {
        return this.getEmptyResponse()
      }

      // Throw custom fetch error - don't log here, let store handle it
      throw new UserBookingsFetchError(url, { langCode }, error)
    }

    // Step 2: Validate response data
    return this.mapReadAllResponse(response, url, { langCode })
  }

  private mapReadAllResponse(
    response: UserBookingsSummaryResponse,
    url: string,
    params: Record<string, unknown>,
  ): UserBookingSummary[] {
    const bookings: UserBookingSummary[] = []
    const validationErrors: string[] = []

    response.forEach((booking, index) => {
      try {
        bookings.push(userBookingSummaryTransformer.parse(booking))
      } catch (error) {
        validationErrors.push(`Booking ${index}: ${error.message}`)
      }
    })

    // If we have validation errors but some bookings succeeded, throw validation error
    // The store can decide whether to use partial data or show error
    if (validationErrors.length > 0) {
      throw new UserBookingsValidationError(
        url,
        params,
        response,
        new Error('Some bookings failed validation'),
        bookings, // Partial success data
        validationErrors,
      )
    }

    return bookings
  }

  private getEmptyResponse(): UserBookingSummary[] {
    return []
  }
}

function parseCancelResponseErrors(errors: string) {
  const doc = new DOMParser().parseFromString(errors, 'text/html')
  const errorNodes = Array.from(doc.querySelectorAll('body > .errorlist > li'))
  const errorsOutput: Record<string, string[]> = {}
  for (const errorNode of errorNodes) {
    const fieldName = toCamel(errorNode.childNodes[0].textContent || '')
    if (!fieldName) {
      continue
    }
    errorsOutput[fieldName] = Array.from(
      errorNode.childNodes[1].childNodes,
    ).map((node) => node.textContent || '')
  }
  return errorsOutput
}

const _cancelResponseSchema = z.object({
  user_bookings_cancel: z.object({
    errors: z.string().optional(),
    status: z.string(),
  }),
})
type CancelResponse = z.infer<typeof _cancelResponseSchema>

const priceSchema = z.object({
  amount: z.number(),
  amountMinor: z.number(),
  currency: z.string(),
})

const moneySchema = z.object({
  amount: z.number().optional(),
  amountMinor: z.number(),
  currency: z.string(),
})

const followupSchema = z.object({
  followupToken: z.string().nullable(),
  review: z
    .object({
      id: z.number(),
      status: z.string(),
    })
    .nullable(),
})
type Followup = z.infer<typeof followupSchema>

const campsiteNoticeSchema = z.object({
  isCovid: z.boolean(),
  text: z.string(),
})

const campsiteSchema = z.object({
  accessCode: z.string().nullable().optional(),
  arrivalTime: z.string().nullable().optional(),
  cancellationPolicyNeedsTranslation: z.boolean().optional(),
  covidWarnings: z.string().array().optional(),
  currency: z.string(),
  email: z.string(),
  fullAddress: z.string(),
  hierarchy: z.object({
    town: z.string(),
    county: z.string(),
  }),
  id: z.number().transform((v) => v.toString()),
  isBookable: z.boolean(),
  location: z.object({
    directions: z.string(),
  }),
  maxChildAge: z.number().nullable(),
  maxInfantAge: z.number().nullable(),
  name: z.string(),
  notices: z.string(),
  noticesNeedsTranslation: z.boolean().optional(),
  campsiteNotices: campsiteNoticeSchema.array(),
  path: z.string(),
  point: z.string(),
  postcode: z.string().nullable(),
  primaryPhoto: z.object({
    url: z.object({
      masterImage: z.string(),
    }),
  }),
  rateCount: z.number(),
  rating: z
    .number()
    .nullable()
    .transform((v) => Number(v)),
  secondaryPhone: z.string().nullable(),
  slug: z.string(),
  tel: z.string(),
  termsAndConditionsNeedsTranslation: z.boolean().optional(),
  usePermittedArrivalDepartTimes: z.boolean(),
  cardTypes: z
    .object({
      name: z.string(),
      slug: z.string(),
    })
    .array()
    .optional(),
})
type Campsite = z.infer<typeof campsiteSchema>

const allowedSubcategorySchema = z.object({
  name: z.string(),
  allowed: z.boolean(),
})

const pitchTypeCategorySchema = z.object({
  id: z.string(),
  name: z.string(),
  slug: z.string(),
})

const pitchTypeNegativeCategorySchema = z.object({
  name: z.string(),
  slug: z.string(),
})

const pitchTypeSchema = z.object({
  allowedSubcategories: allowedSubcategorySchema.array(),
  bedroomsText: z.string(),
  bringBedLinenHtml: z.string().nullable().optional(),
  categories: pitchTypeCategorySchema.array(),
  description: z.string(),
  dimensions: z
    .string()
    .nullable()
    .transform((v) => (v ? v.toString() : '')),
  dimensionsDetailed: z
    .object({
      areaFt: z.number().nullable(),
      areaM: z.number().nullable(),
      customDimensions: z.string().nullable(),
      depthFt: z.number().nullable(),
      depthM: z.number().nullable(),
      dimensionsType: z.string().nullable(),
      dimensionsTypeCode: z.string().nullable(),
      widthFt: z.number().nullable(),
      widthM: z.number().nullable(),
    })
    .optional()
    .nullable(),
  groundType: z
    .string()
    .nullable()
    .transform((v) => (v === null ? '' : v)),
  isByo: z.boolean(),
  name: z.string(),
  negativeCategories: pitchTypeNegativeCategorySchema.array(),
  oneUnitPerPitch: z.boolean(),
  primaryPhoto: z
    .object({
      url: z.object({
        masterImage: z.string(),
      }),
    })
    .optional(),
  requireCarRegistration: z.boolean(),
  requirePartyNames: z.boolean(),
  restrictions: z
    .object({
      name: z.string(),
      amenityIcon: z.string().nullable(),
    })
    .array()
    .optional(),
  twinAxleAccepted: z
    .boolean()
    .nullable()
    .transform((v) => !!v),
})
type PitchType = z.infer<typeof pitchTypeSchema>

const stripeCampsitePaymentSchema = z.object({
  status: z.string(),
  statusDisplay: z.string(),
  card: z
    .object({
      type: z.string().nullable(),
      number: z
        .number()
        .nullable()
        .transform((v) => (v ? v.toString() : null)),
      expMonth: z.string().nullable(),
      expYear: z.string().nullable(),
    })
    .nullable(),
  paymentLinkPaidDetails: z
    .object({
      amount: priceSchema,
      datetime: z.string(),
    })
    .nullable()
    .optional(),
})
type StripeCampsitePayment = z.infer<typeof stripeCampsitePaymentSchema>

const taxSchema = z.object({
  amount: priceSchema,
  hidden: z.boolean(),
  inclusive: z.boolean(),
  rate: z.string().transform((v) => Number(v)),
  taxType: z.string(),
})
type Tax = z.infer<typeof taxSchema>

const paymentStatusSchema = z.object({
  card: z
    .object({
      id: z.number().nullable(),
    })
    .nullable(),
  dueDate: z.string(),
  status: z.string(),
})
type PaymentStatus = z.infer<typeof paymentStatusSchema>

const paymentInfoSchema = z.object({
  paymentTiming: z.enum(['ADVANCE', 'ARRIVE']).nullable(),
})

const bookingSummaryCampsiteFetchSchema = z.object({
  accessCode: z.string().nullable().optional(),
  fullAddress: z.string().nullable(),
  name: z.string(),
  path: z.string(),
  primaryPhoto: z.object({
    url: z.object({
      masterImage: z.string(),
    }),
  }),
  slug: z.string(),
})

const bookingSummaryPitchTypeSchema = z.object({
  categories: pitchTypeCategorySchema.array(),
  primaryPhoto: z
    .object({
      url: z.object({
        masterImage: z.string(),
      }),
    })
    .optional(),
  name: z.string(),
  negativeCategories: pitchTypeNegativeCategorySchema.array(),
})

const userBookingSummaryFetchResponseSchema = z.object({
  adults: z.number(),
  arrive: z.string(),
  balanceDueDate: z.string().nullable(),
  campsite: bookingSummaryCampsiteFetchSchema,
  cancelledAt: z.string().nullable(),
  canReview: z.boolean(),
  childAges: z.string(),
  depart: z.string(),
  followup: followupSchema.nullable(),
  isBYO: z.boolean().optional(),
  isCancelled: z.boolean(),
  paymentInfo: paymentInfoSchema,
  partyDetailed: z
    .object({
      adults: z.number(),
      children: z.number(),
      childrenAges: z.number().array(),
      infants: z.number(),
      infantAges: z.number().array(),
      dogs: z.number(),
      display: z.string(),
    })
    .optional(),
  pitchtype: bookingSummaryPitchTypeSchema,
  pitchtypeId: z.string(),
  pitchtypeNameWithUnitNumber: z.string().optional(),
  prettyId: z.string(),
  price: moneySchema,
  remainder: priceSchema,
  stripecampsitepayment: stripeCampsitePaymentSchema.nullable(),
  status: z.number(),
  unitType: z
    .object({
      id: z.string(),
      name: z.string(),
      slug: z.string(),
    })
    .optional()
    .nullable(),
})
type UserBookingSummaryFetchResponse = z.infer<
  typeof userBookingSummaryFetchResponseSchema
>
type UserBookingsSummaryResponse = UserBookingSummaryFetchResponse[]

const userBookingSummaryTransformer =
  userBookingSummaryFetchResponseSchema.transform((booking) => {
    const stripeCampsitePayment = booking.stripecampsitepayment

    const result: any = {
      ...booking,
      balanceDueDate: booking.balanceDueDate ? booking.balanceDueDate : null,
      campsite: {
        ...booking.campsite,
        primaryPhoto: getTransformObject<UserBookingCampsitePhoto>(
          'bookingCampsite',
          booking.campsite.primaryPhoto.url.masterImage,
        ),
      },
      cancelledAt: booking.cancelledAt ? booking.cancelledAt : '',
      childAges: childAgeCSVStringToNumberArray(booking.childAges),
      pitchType: booking.pitchtype,
      stripePaymentStatus: stripeCampsitePayment
        ? stripeCampsitePayment.status
        : null,
      stripecampsitepayment: getStripeCampsitePayment(
        booking.stripecampsitepayment,
      ),
      status: booking.status,
      totalCost: booking.price,
    }
    const categories = booking.pitchtype.categories
    if (categories) {
      result.categories = categories.map((c) => c.id)
    }
    const primaryPhoto = booking.pitchtype.primaryPhoto
    if (primaryPhoto) {
      result.primaryPhoto = primaryPhoto
    }
    return result
  })

const arrivalTaxResponseSchema = z.object({
  amount: z.object({ amountMinor: z.number(), currency: z.string() }),
  inclusive: z.boolean(),
  taxType: z.string(),
})

const extraSchema = z.object({
  name: z.string(),
  price: moneySchema,
  quantity: z.number(),
})

const balanceRefundSchema = z.object({
  amount: moneySchema,
  created: z.string(),
})

const userBookingFetchResponseSchema = z.object({
  accommodationCost: priceSchema,
  address: z.string(),
  adults: z.number(),
  arrivalTaxes: arrivalTaxResponseSchema.array(),
  childAges: z.string(),
  pitchtypeId: z.string(),
  arrivalFrom: z.string().nullable(),
  arrivalTime: z.string(),
  arrivalTo: z.string().nullable(),
  arrive: z.string(),
  balanceDueDate: z.string().nullable(),
  balancePaymentTermsHtml: z.string(),
  balanceRefunds: balanceRefundSchema.array(),
  campsite: campsiteSchema,
  campsite_PaymentTermsHtml: z.string(),
  campsite_TaxTermsHtml: z.string().optional(),
  campsiteCityTax: z.string().nullable().optional(),
  cancellationPolicy: z.string(),
  cancellationReason: z.string().nullable(),
  cancelledAt: z.string().nullable(),
  canReview: z.boolean(),
  carRegistrationNumber: z.string().nullable(),
  changes: z
    .object({
      arrive: z
        .object({
          originalDate: z.string(),
          changedDate: z.string(),
        })
        .optional(),
      depart: z
        .object({
          originalDate: z.string(),
          changedDate: z.string(),
        })
        .optional(),
    })
    .nullable(),
  commission: z.string().transform((v) => Number(v)),
  currencyPreference: z.string(),
  city: z.string(),
  country: z.string(),
  county: z.string(),
  created: z.string(),
  data: z.string().nullable(),
  depart: z.string(),
  departFrom: z.string().nullable(),
  deposit: priceSchema,
  dogs: z.number().nullable().optional(),
  doorCode: z.string().nullable().optional(),
  email: z.string(),
  estimatedTimeOfArrival: z.string().nullable(),
  extras: extraSchema.array().nullable(),
  firstName: z.string(),
  followup: followupSchema.nullable(),
  hostedFullPaymentWhen: z.string(),
  id: z.string(),
  isBYO: z.boolean().optional(),
  isCancelled: z.boolean(),
  lastName: z.string(),
  namesOfAllPartyMembers: z.string().nullable(),
  party: z.string(),
  partyDetailed: z
    .object({
      adults: z.number(),
      children: z.number(),
      childrenAges: z.number().array(),
      infants: z.number(),
      infantAges: z.number().array(),
      dogs: z.number(),
      display: z.string(),
    })
    .optional(),
  paymentInfo: paymentInfoSchema,
  paymentStatus: paymentStatusSchema.nullable(),
  paymentType: z.string(),
  pitchtype: pitchTypeSchema,
  postcode: z.string().nullable(),
  prettyId: z.string(),
  pitchtypeNameWithUnitNumber: z.string().optional(),
  price: moneySchema,
  remainder: priceSchema,
  specialRequests: z.string(),
  stripecampsitepayment: stripeCampsitePaymentSchema.nullable(),
  status: z.number(),
  stayType: z.string(),
  stripePaymentLink: z.string().nullable(),
  taxes: taxSchema.array(),
  termsAndConditions: z.string(),
  telephone: z.string(),
  totalCostText: z.string(),
  typeAndApproxSizeOfUnit: z.string().nullable(),
  unitType: z
    .object({
      id: z.string(),
      name: z.string(),
      slug: z.string(),
    })
    .optional()
    .nullable(),
})

const userBookingTransformer = userBookingFetchResponseSchema.transform(
  (booking) => {
    const stripeCampsitePayment = booking.stripecampsitepayment

    const result: any = {
      ...booking,
      campsiteCityTax: booking.campsiteCityTax ? booking.campsiteCityTax : null,
      arrivalFrom: booking.arrivalFrom ? booking.arrivalFrom : null,
      arrivalTo: booking.arrivalTo ? booking.arrivalTo : null,
      balanceDueDate: booking.balanceDueDate ? booking.balanceDueDate : null,
      balancePaymentTerms: booking.balancePaymentTermsHtml,
      campsite: mapQueryCampsiteToUserBookingCampsite(booking.campsite),
      campsitePaymentTerms: booking.campsite_PaymentTermsHtml,
      campsiteTaxTerms: booking.campsite_TaxTermsHtml,
      cancellationReason: booking.cancellationReason
        ? booking.cancellationReason
        : '',
      cancelledAt: booking.cancelledAt ? booking.cancelledAt : '',
      cardDetails: getCardDetails(stripeCampsitePayment),
      carRegistrationNumber: booking.carRegistrationNumber || '',
      childAges: childAgeCSVStringToNumberArray(booking.childAges),
      departFrom: booking.departFrom ? booking.departFrom : null,
      estimatedTimeOfArrival: booking.estimatedTimeOfArrival || '',
      groupName: getGroupName(booking.data),
      isBYO: booking.isBYO,
      namesOfAllPartyMembers: booking.namesOfAllPartyMembers || '',
      partyDetailed: booking.partyDetailed,
      paymentCardId: getPaymentCardId(booking.paymentStatus),
      paymentDueDate: booking.paymentStatus
        ? booking.paymentStatus.dueDate
        : null,
      paymentStatus: booking.paymentStatus
        ? booking.paymentStatus.status
        : null,
      party: booking.party,
      pitchType: mapQueryPitchTypeToUserBookingPitchType(booking.pitchtype),
      review: mapFollowupToUserBookingReview(booking.followup),
      stripePaymentStatus: stripeCampsitePayment
        ? stripeCampsitePayment.status
        : null,
      taxes: mapQueryTaxesToUserBookingTaxes(booking.taxes),
      token: booking.followup?.followupToken || '',
      totalCost: booking.price,
      typeAndApproxSizeOfUnit: booking.typeAndApproxSizeOfUnit || '',
      unitType: booking.unitType,
      stripecampsitepayment: getStripeCampsitePayment(
        booking.stripecampsitepayment,
      ),
      categories: [] as string[],
      primaryPhoto: undefined,
    }
    const categories = booking.pitchtype.categories
    if (categories) {
      result.categories = categories.map((c) => c.id)
    }
    const primaryPhoto = booking.pitchtype.primaryPhoto
    if (primaryPhoto) {
      result.primaryPhoto = primaryPhoto.url
    }
    return result
  },
)

function mapQueryCampsiteToUserBookingCampsite(campsite: Campsite) {
  return {
    ...campsite,
    address: campsite.fullAddress,
    cancellationPolicyNeedsTranslation:
      !!campsite.cancellationPolicyNeedsTranslation,
    directions: campsite.location.directions,
    hierarchy: { ...campsite.hierarchy },
    noticesNeedsTranslation: !!campsite.noticesNeedsTranslation,
    rating: {
      rate: campsite.rating,
      votes: campsite.rateCount,
    },
    point: getGeoPointFromWKTPoint(campsite.point),
    primaryPhoto: getTransformObject<UserBookingCampsitePhoto>(
      'bookingCampsite',
      campsite.primaryPhoto.url.masterImage,
    ),
    termsAndConditionsNeedsTranslation:
      !!campsite.termsAndConditionsNeedsTranslation,
    warnings: campsite.covidWarnings ? campsite.covidWarnings : [],
  }
}

function getCardDetails(payment: StripeCampsitePayment | null) {
  if (payment && payment.card) {
    return {
      ...payment.card,
      brand: payment.card.type,
      last4: payment.card.number,
    }
  }
  return null
}

function getGroupName(data: string | null): string {
  if (!data) {
    return ''
  }

  return JSON.parse(data).group_name || ''
}

function getPaymentCardId(paymentStatus: PaymentStatus | null) {
  return paymentStatus && paymentStatus.card?.id
    ? paymentStatus.card.id.toString()
    : null
}

function mapQueryPitchTypeToUserBookingPitchType(pitchType: PitchType) {
  return {
    ...pitchType,
    allowedSubcategories: pitchType.allowedSubcategories
      .filter((category) => category.allowed)
      .map((category) => category.name),
  }
}

function mapFollowupToUserBookingReview(followup: Followup | null) {
  let review: UserBookingReview | null = null
  if (followup && followup.review !== null) {
    const reviewResponse = followup.review
    review = {
      id: reviewResponse.id.toString(),
      status: reviewResponse.status,
    }
  }
  return review
}

function mapQueryTaxesToUserBookingTaxes(taxes: Tax[]) {
  return taxes.map((tax) => {
    const { taxType, ...taxObj } = tax
    return {
      ...taxObj,
      type: taxType,
    }
  })
}

function getStripeCampsitePayment(payment: StripeCampsitePayment | null) {
  return payment
    ? {
        ...payment,
        status: payment?.status || '',
        statusDisplay: payment?.statusDisplay || '',
        card: getCardDetails(payment),
      }
    : null
}
